import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { logAnalyticsEvent } from '../../../analytics/nativeAnalytics';
import * as api from '../services/taActionBoardApi';
import type { BoardResponse, RoleBoard, TrackingMap } from '../types/taActionBoard';
import { ActionModals, type ModalState } from './taActionBoard/ActionModals';
import { AwaitingFeedbackStrip } from './taActionBoard/AwaitingFeedbackStrip';
import { BoardErrorState, type BoardErrorInfo } from './taActionBoard/BoardErrorState';
import { omitPending, readBoardCache, writeBoardCache } from './taActionBoard/boardCache';
import { useBoardSelection } from './taActionBoard/useBoardSelection';
import { DISCARDED_LABEL, FeedbackTab } from './taActionBoard/FeedbackTab';
import { useBoardNavigation } from './taActionBoard/useBoardNavigation';
import { FinalRoundsSection } from './taActionBoard/FinalRoundsSection';
import { FirstInterviewsSection } from './taActionBoard/FirstInterviewsSection';
import { firstNameFromEmail } from './taActionBoard/format';
import { SchedulingGroup, type SchedulingSubTab } from './taActionBoard/SchedulingGroup';
import { SchedulingTab, type SchedulingTabAction } from './taActionBoard/SchedulingTab';
import { RequestsTab } from './taActionBoard/RequestsTab';
import { CandidateChipsProvider } from './taActionBoard/CandidateChipsProvider';
import { RescheduleProvider } from './taActionBoard/RescheduleContext';
import type { SchedulingConnectionCtx } from './taActionBoard/RequestSchedulingConnection';
import { BoardHeader, BoardTabs, PendingActionsBanner } from './taActionBoard/BoardChrome';
import { useBoardTab } from './taActionBoard/useBoardTab';
import { GmailConnectionProvider } from './taActionBoard/GmailConnectionContext';
import { PilotLockBanner, PilotLockProvider } from './taActionBoard/PilotLock';
import { useGmailToken } from './taActionBoard/useGmailToken';
import { useBoardVersion } from './taActionBoard/useBoardVersion';
import { usePendingActions } from './taActionBoard/usePendingActions';
import { SlaTab } from './taActionBoard/SlaTab';
import { TeamSidebar } from './taActionBoard/TeamSidebar';
import { useBoardActions } from './taActionBoard/useBoardActions';
import { useHandledCards } from './taActionBoard/useHandledCards';
import { BoardLoadingState } from './taActionBoard/BoardLoadingState';
import { useSlotHolds } from './taActionBoard/useSlotHolds';
import { useModelCards } from './taActionBoard/useModelCards';

export default function TAActionBoardView() {
  const { user } = useAuth();
  const myEmail = (user?.email || '').toLowerCase();

  // Roster + which board is on screen, remembered across a hard refresh (see useBoardSelection).
  const { recruiters, selectedEmail, selectBoard } = useBoardSelection(myEmail);
  const [board, setBoard] = useState<BoardResponse | null>(null);
  const [tracking, setTracking] = useState<TrackingMap>({});
  const [loading, setLoading] = useState(true);
  // Not a bare string: whether Retry is offered depends on WHY the build failed ("no Lever account" must not ship a dead button).
  const [error, setError] = useState<BoardErrorInfo | null>(null);
  const [refreshedAt, setRefreshedAt] = useState<Date | null>(null);
  const [cachedStale, setCachedStale] = useState<boolean | null>(null); // null = live this session; true = cached > 10 min
  const isOwnBoard = selectedEmail === myEmail;
  // Tab state lives in the URL — see useBoardTab for why, and for the Requests-is-own-board rule.
  const { tab, effectiveTab, setTab } = useBoardTab(isOwnBoard);
  /** Which interview round is showing inside 📅 Scheduling. */
  const [round, setRound] = useState<SchedulingSubTab>('first');
  // Tab + round + focus move together when an Overdue chip routes somewhere (useBoardNavigation).
  const { focus, clearFocus, goToCandidate, goToTab, goToRound } = useBoardNavigation({
    tab: effectiveTab,
    round,
    setTab,
    setRound,
  });
  // Owned by RequestsTab, lifted for the tab badge; null until the mailbox is read (BoardCounts.requests).
  const [requestCount, setRequestCount] = useState<number | null>(null);
  const [modal, setModal] = useState<ModalState>(null);
  useModelCards(board, setBoard, setModal); // summaries + drafts the build no longer waits for
  const [rescheduleOpen, setRescheduleOpen] = useState(false); // RescheduleProvider's dialog — a modal too
  const { handled, setHandled, markHandled, unmarkHandled } = useHandledCards();
  const { pending, pendingIds, defer: deferAction, cancel: cancelPending } = usePendingActions();
  // Payload-version bookkeeping (see useBoardVersion.ts); staleVersion is consumed further down.
  const { checkNow, recheck, markBoardVersion, markBoardFresh, boardVersion, staleVersion, clearStale } =
    useBoardVersion();
  // The board's ONE Gmail authorisation, shared with every tab via context —
  // connecting in the header (or any tab) connects them all.
  const gmail = useGmailToken(myEmail);
  // Monotonic sequence — bumping it invalidates any in-flight board fetch, so a
  // slow Lever pull can never overwrite a board the user has switched away from.
  const loadSeqRef = useRef(0);

  /**
   * Full Lever pull for ONE board; updates only that recruiter's cache.
   *
   * @param fresh  skip the proxy's cached copy. Refresh passes it; a RETRY after a failure must
   *   not — see `refresh` and `retry` below.
   */
  const loadBoard = useCallback((email: string, fresh = false) => {
    loadSeqRef.current += 1;
    const seq = loadSeqRef.current;
    setLoading(true);
    setError(null);
    api
      .fetchBoard(email, { fresh })
      .then(async (b) => {
        const oppIds = b.roles.flatMap((r) => [
          ...r.scheduling.candidates.map((c) => c.opportunityId),
          ...r.finalRounds.candidates.map((c) => c.opportunityId),
        ]);
        const t = await api.fetchTracking(oppIds).catch(() => ({}));
        if (seq !== loadSeqRef.current) return;
        markBoardFresh(); // fresh from the server — satisfies the latest version known
        setBoard(b);
        setTracking(t);
        setHandled({}); // fresh Lever data — everything managed is gone from it
        setRefreshedAt(new Date());
        setCachedStale(null);
        setError(null);
        setLoading(false);
      })
      .catch((e) => {
        if (seq !== loadSeqRef.current) return;
        setError({
          message: e instanceof Error ? e.message : String(e),
          // Anything the proxy did not classify keeps the Retry button: an
          // unknown failure is more often transient than permanent.
          retryable: e instanceof api.BoardLoadError ? e.retryable : true,
        });
        setLoading(false);
      });
  }, [markBoardFresh, setHandled]);

  // Triggered runs only (product decision): selecting a recruiter renders their
  // cached board instantly with NO automatic refetch; only the first-ever visit
  // (no cache yet) pulls from Lever. The Refresh button refetches explicitly.
  /** Render a cached board, or pull a fresh one when there is nothing usable. */
  const applyCache = useCallback(
    (cached: ReturnType<typeof readBoardCache>, email: string) => {
      if (!cached) {
        loadBoard(email);
        return;
      }
      loadSeqRef.current += 1; // drop any in-flight fetch for a previous board
      markBoardVersion(cached.serverVersion); // it satisfies its own stamp, not anything newer
      const fetchedAt = new Date(cached.fetchedAt);
      setBoard(cached.board);
      setTracking(cached.tracking);
      setHandled(cached.handled);
      setRefreshedAt(fetchedAt);
      setCachedStale(Date.now() - fetchedAt.getTime() > 10 * 60_000);
      setError(null);
      setLoading(false);
    },
    [loadBoard, markBoardVersion, setHandled]
  );

  useEffect(() => {
    if (!selectedEmail) return undefined;
    let dropped = false;
    // Ask the proxy which build it is on BEFORE trusting the cached board (cheap: no Lever).
    void (async () => {
      const version = await checkNow();
      if (dropped) return;
      applyCache(readBoardCache(selectedEmail, version), selectedEmail);
    })();
    return () => {
      dropped = true;
    };
  }, [selectedEmail, applyCache, checkNow]);

  // The view never remounts across /ta-action-boards/:tab switches — re-ask (throttled) on moves.
  useEffect(() => {
    recheck();
  }, [tab, round, recheck]);

  // An explicit mismatch re-runs the sanctioned mount-time path once the board is quiet (never
  // under an open modal or countdown); deferred because applyCache sets state. clearStale() runs
  // BEFORE the pull (consume-once) — see its doc for the failing-pull retry storm it prevents.
  useEffect(() => {
    if (!staleVersion || loading || modal || rescheduleOpen || pending.length > 0) return undefined;
    let dropped = false;
    void Promise.resolve().then(() => {
      if (dropped) return;
      clearStale();
      applyCache(readBoardCache(selectedEmail, staleVersion), selectedEmail);
    });
    return () => {
      dropped = true;
    };
  }, [staleVersion, loading, modal, rescheduleOpen, pending, selectedEmail, applyCache, clearStale]);

  // Persist the board (incl. optimistic updates) under ITS OWN recruiter's key,
  // keeping the original fetch timestamp. Other recruiters' caches stay untouched.
  useEffect(() => {
    if (board && refreshedAt) {
      writeBoardCache(
        board.recruiter.email,
        board,
        tracking,
        refreshedAt.toISOString(),
        /**
         * Only markers for work that has actually HAPPENED.
         *
         * A confirm marks its cards handled straight away, but the action itself
         * waits ten seconds behind the countdown — and this effect runs on the
         * very next render. Persisting an optimistic marker meant that closing
         * the tab or reloading inside those ten seconds left the card greyed out
         * as "Invite sent" for ever while nothing had been sent, nothing logged
         * and no error shown. The queue is in memory, so the action is gone;
         * dropping its marker too is what makes the board honest again.
         */
        omitPending(handled, pendingIds),
        // The stamp the board is KNOWN to satisfy — never the newest version it merely outlived.
        boardVersion()
      );
    }
  }, [board, tracking, refreshedAt, handled, pendingIds, boardVersion]);

  // Refresh is `fresh` and retry-after-a-failure is NOT, and that pair is load-bearing: the
  // browser gives up at BOARD_TIMEOUT_MS but the proxy finishes and caches the board, so the
  // attempt that just failed is usually what makes the retry instant. See fetchBoard.
  const refresh = useCallback(() => {
    if (selectedEmail) loadBoard(selectedEmail, true);
  }, [selectedEmail, loadBoard]);
  const retry = useCallback(() => {
    if (selectedEmail) loadBoard(selectedEmail, false);
  }, [selectedEmail, loadBoard]);

  // Calendar holds are read separately from the board snapshot — see useSlotHolds.
  const postingIds = useMemo(() => (board?.roles || []).map((r) => r.postingId), [board]);
  const slotHolds = useSlotHolds(postingIds);

  const counts = useMemo(() => {
    const roles = board?.roles || [];
    return {
      feedback: roles.reduce((n, r) => n + r.feedback.length, 0),
      firstInterviews: roles.reduce((n, r) => n + (r.firstInterviews?.candidates.length ?? 0), 0),
      scheduling: roles.reduce((n, r) => n + r.scheduling.candidates.length, 0),
      finalRounds: roles.reduce((n, r) => n + r.finalRounds.candidates.length, 0),
      sla: roles.reduce((n, r) => n + r.sla.actNow.length + r.sla.screeningOverSla, 0),
    };
  }, [board]);

  const boardEmail = selectedEmail;

  // All action handlers (optimistic update + API + toast) live in the hook.
  const { handlers, applyLocalTracking, applyTracking, updateFinalRounds, recheckSlots } = useBoardActions({
    myEmail,
    boardEmail,
    tracking,
    setTracking,
    setBoard,
    markHandled,
    unmarkHandled,
    deferAction,
    gmailToken: gmail.token,
    slotHolds,
  });

  const selectedRecruiter = recruiters.find((r) => r.email === selectedEmail);
  const firstName = selectedRecruiter ? selectedRecruiter.name.split(' ')[0] : firstNameFromEmail(selectedEmail);
  // Invite email is sent from the signed-in user's own mail client, signed with THEIR first name (OOO coverage of another's board).
  const senderFirstName = user?.displayName?.split(' ')[0] || firstNameFromEmail(myEmail);

  /**
   * One switch for every 📅 scheduling action, shared by the Scheduling tab and the Requests
   * tab's embedded overview — two copies would eventually route the same button differently.
   * edit-panel, holds (selected slots), the blocker overview and ask are ROLE-level — no
   * candidate involved.
   */
  const onSchedulingAction = (role: RoleBoard, action: SchedulingTabAction) => {
    switch (action.type) {
      case 'holds':
        return setModal({ kind: 'holds', role, slots: action.slots });
      case 'edit-panel':
        return setModal({ kind: 'edit-panel', role, suggestion: action.suggestion });
      case 'ask':
        return setModal({ kind: 'ask', role, slots: action.slots });
      // Deleting a calendar hold is permanent, so unticking asks first
      case 'release-hold':
        return setModal({ kind: 'release-hold', role, hold: action.hold });
      case 'release-legacy-holds':
        return setModal({ kind: 'release-legacy-holds', role });
      // Its own case, because the proposal modal has to open on the slots the
      // recruiter ticked — the shared `default` drops everything but the candidate.
      case 'offered':
        return setModal({
          kind: 'offered',
          role,
          candidate: action.candidate,
          preselected: action.preselected,
        });
      default:
        return setModal({ kind: action.type, role, candidate: action.candidate });
    }
  };


  /**
   * The Requests tab's window into the Scheduling tab: the board payload plus the same modal
   * routes the Scheduling tabs use, so an embedded candidate card acts identically to its
   * original. Absent until the board has loaded — the request cards then render no connection.
   */
  const schedulingCtx: SchedulingConnectionCtx | undefined = board
    ? {
        roles: board.roles,
        stages: board.stages || [],
        tracking,
        handled,
        myEmail,
        onSchedulingAction,
        onRecheck: recheckSlots,
        holds: slotHolds.byPosting,
        legacyHolds: slotHolds.legacy,
        archiveReasons: board.archiveReasons || [],
        finalRound: {
          boardEmail,
          senderFirstName,
          gmailToken: gmail.token,
          onApplyTracking: applyTracking,
          onLocalTracking: applyLocalTracking,
          onMarkHandled: markHandled,
          onDefer: deferAction,
          onFinalRoundsUpdate: updateFinalRounds,
        },
        onGoToRound: (r) => goToRound(r, 'scheduling'),
      }
    : undefined;

  return (
    <GmailConnectionProvider value={gmail}>
    <PilotLockProvider pilot={board?.pilot}>
    <div className="flex min-h-full">
      {/* ToastManager moved to App.tsx — the design system requires ONE instance per app, and
          mounting it here meant every other view's toasts were written to the store and never
          rendered (a silent failure looks identical to a dead button). */}
      <PendingActionsBanner pending={pending} onCancel={cancelPending} />
      <TeamSidebar
        recruiters={recruiters}
        myEmail={myEmail}
        selectedEmail={selectedEmail}
        onSelect={(email) => {
          selectBoard(email);
          clearFocus();
        }}
      />

      <main className="min-w-0 flex-1 space-y-5 bg-[#F8F8F8] p-6">
        <BoardHeader
          firstName={firstName}
          refreshedAt={refreshedAt}
          cachedStale={cachedStale}
          loading={loading}
          onRefresh={refresh}
        />

        <PilotLockBanner />

        {loading ? (
          <BoardLoadingState
            firstName={firstName}
            gmailConnected={gmail.connected}
            gmailConnecting={gmail.connecting}
            onConnectGmail={() => void gmail.connect()}
          />
        ) : error || !board ? (
          <BoardErrorState error={error} onRetry={retry} />
        ) : (
          <>
            <BoardTabs
              tab={effectiveTab}
              showRequests={isOwnBoard}
              counts={{ ...counts, requests: requestCount }}
              finalRoundsUnavailable={board.finalRoundsUnavailable}
              onChange={(next) => {
                goToTab(next);
                // Which tabs actually get used — the board's basic adoption signal.
                void logAnalyticsEvent('ta_board_tab_opened', { tab: next });
              }}
            />

            <CandidateChipsProvider boardEmail={boardEmail} verdicts={board?.candidateVerdicts}><RescheduleProvider boardEmail={boardEmail} onDefer={deferAction} onChanged={refresh} onOpenChange={setRescheduleOpen}>
            {effectiveTab === 'feedback' && (
              <FeedbackTab
                roles={board.roles}
                boardEmail={boardEmail}
                onChanged={refresh}
                handled={handled}
                focus={focus}
                onClearFocus={clearFocus}
                onAction={(role, action) => {
                  // Discard removes the card from the overview without an archive or a move:
                  // mark it handled (so it leaves the counts and persists in the cache) and
                  // open no modal.
                  if (action.type === 'discard') {
                    markHandled(action.item.opportunityId, DISCARDED_LABEL);
                    return;
                  }
                  setModal({ kind: action.type, role, item: action.item });
                }}
              />
            )}
            {effectiveTab === 'scheduling' && (
              <SchedulingGroup
                active={round}
                counts={{ first: counts.firstInterviews, case: counts.scheduling, final: counts.finalRounds }}
                finalRoundsUnavailable={board.finalRoundsUnavailable}
                onChange={(next) => {
                  goToRound(next);
                  void logAnalyticsEvent('ta_board_round_opened', { round: next });
                }}
              >
                <AwaitingFeedbackStrip roles={board.roles} boardEmail={boardEmail} onChanged={refresh} />
                {round === 'first' && (
                  <FirstInterviewsSection
                    roles={board.roles} onChanged={refresh}
                    handled={handled}
                    focus={focus}
                    boardEmail={boardEmail}
                    senderName={user?.displayName || myEmail}
                    onMarkHandled={markHandled}
                    onUnmarkHandled={unmarkHandled}
                    onDefer={deferAction}
                    onGoToSla={() => goToTab('sla')}
                  />
                )}
                {round === 'case' && (
              <SchedulingTab
                roles={board.roles}
                handled={handled}
                focus={focus}
                onChanged={refresh}
                boardEmail={boardEmail}
                tracking={tracking}
                myEmail={myEmail}
                onRecheck={recheckSlots}
                onAction={onSchedulingAction}
                holds={slotHolds.byPosting}
                legacyHolds={slotHolds.legacy}
              />
                )}
                {round === 'final' && (
                  <FinalRoundsSection
                    roles={board.roles} onChanged={refresh}
                    handled={handled}
                    focus={focus}
                    tracking={tracking}
                    boardEmail={boardEmail}
                    myEmail={myEmail}
                    senderFirstName={senderFirstName}
                    stages={board.stages || []} archiveReasons={board.archiveReasons || []}
                    unavailable={board.finalRoundsUnavailable}
                    onApplyTracking={applyTracking}
                    onLocalTracking={applyLocalTracking}
                    onMarkHandled={markHandled}
                    gmailToken={gmail.token}
                    onDefer={deferAction}
                    onFinalRoundsUpdate={updateFinalRounds}
                  />
                )}
              </SchedulingGroup>
            )}
            {effectiveTab === 'sla' && (
              <SlaTab
                roles={board.roles}
                handled={handled}
                owner={boardEmail}
                onGoTo={goToCandidate}
                onNudgeFeedback={(role, item) => setModal({ kind: 'nudge', role, item })}
                onChanged={refresh}
                onDefer={deferAction}
              />
            )}
            {effectiveTab === 'requests' && (
              <RequestsTab
                // Remount per board so the tab re-fetches with a fresh loading state instead of
                // an effect having to reset it — and per ACCOUNT as well: the mailbox is what this
                // tab reads, and a board that stays selected across a sign-in as someone else would
                // otherwise leave the previous user's mail on screen with its state seeded from the
                // previous user's cache.
                key={`${selectedEmail}:${myEmail}`}
                isOwnBoard={selectedEmail === myEmail}
                boardFirstName={firstName}
                myEmail={myEmail}
                onCountChange={setRequestCount}
                deferAction={deferAction}
                schedulingCtx={schedulingCtx}
              />
            )}
            </RescheduleProvider></CandidateChipsProvider>
          </>
        )}

        <ActionModals
          modal={modal}
          archiveReasons={board?.archiveReasons || []}
          // older cached boards have no stage list — the Move modal then only
          // offers the computed next stage and the proxy keeps its old behavior
          stages={board?.stages || []}
          senderFirstName={senderFirstName}
          boardEmail={boardEmail}
          gmailConnected={!!gmail.token}
          onClose={() => setModal(null)}
          handlers={handlers}
          holds={slotHolds.byPosting}
          tracking={tracking}
        />
      </main>
    </div>
    </PilotLockProvider>
    </GmailConnectionProvider>
  );
}
